/**
 * Pick a prompt group, then a random rule or any of its rules.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Plus } from 'lucide-react';

import { MultiSearchableSelect } from '@/components/ui';
import type { TreeGroupLike } from '@/features/campaign-texts/utils/group-tree.utils';
import {
  getActiveSelectableLeafGroups,
  getRandomizeScopeFolderOptions,
  optionLabelForGroup,
} from '@/features/campaign-texts/utils/group-tree.utils';

import { PickRow } from './pick-row';
import { idsFromPicks, picksFromIds, type PromptPick } from './prompt-group-picks';

interface Props {
  groups: TreeGroupLike[];
  ids: string[];
  onChange: (ids: string[]) => void;
}

export const PromptGroupPicks: FC<Props> = ({ groups, ids, onChange }) => {
  const folders = getRandomizeScopeFolderOptions(groups);
  if (folders.length === 0) return <LeafRules groups={groups} ids={ids} onChange={onChange} />;
  return <FolderPicks groups={groups} ids={ids} folders={folders} onChange={onChange} />;
};

const LeafRules: FC<Props> = ({ groups, ids, onChange }) => (
  <div className="w-full max-w-xl">
    <MultiSearchableSelect
      options={getActiveSelectableLeafGroups(groups).map((g) => ({
        value: g.id,
        label: optionLabelForGroup(g, groups),
      }))}
      value={ids}
      onChange={onChange}
      placeholder="+ rules"
      summaryNoun="rules"
      searchPlaceholder="Search prompt rules…"
    />
  </div>
);

const FolderPicks: FC<Props & { folders: { id: string; name: string }[] }> = ({
  groups, ids, folders, onChange,
}) => {
  const picks = picksFromIds(ids, groups);
  const setPicks = (next: PromptPick[]) => onChange(idsFromPicks(next));
  const add = nextPick(folders, picks);
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      {picks.map((pick, index) => (
        <PickRow
          key={pick.folderId}
          groups={groups}
          folders={folders}
          pick={pick}
          onChange={(next) => setPicks(picks.map((row, i) => (i === index ? next : row)))}
          onRemove={() => setPicks(picks.filter((_, i) => i !== index))}
        />
      ))}
      <AddGroup disabled={!add} onClick={() => add && setPicks([...picks, add])} />
    </div>
  );
};

const AddGroup: FC<{ disabled: boolean; onClick: () => void }> = ({ disabled, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className="inline-flex w-fit items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-40"
  >
    <Plus className="h-3 w-3" /> Add a group
  </button>
);

function nextPick(folders: { id: string }[], picks: PromptPick[]): PromptPick | null {
  const free = folders.find((f) => !picks.some((p) => p.folderId === f.id));
  return free ? { folderId: free.id, ruleIds: [] } : null;
}
